    export function initTopBarNavigation() {
        document.querySelectorAll(".block--top-bar-navigation").forEach(initNavigation);
    }

    // Lets blocks below size themselves to the viewport minus the header, e.g. the hero split.
    // The height changes with the breakpoint and when the top bar wraps, so it is observed.
    function publishHeight(navigation) {
        var setHeight = function () {
            document.documentElement.style.setProperty(
                "--gs-top-bar-navigation-height",
                navigation.getBoundingClientRect().height + "px"
            );
        };

        setHeight();

        if ("ResizeObserver" in window) {
            new ResizeObserver(setHeight).observe(navigation);
        }
    }

    // Once the header has scrolled out of view it is fixed to the top, hidden, and slides back in
    // when scrolling up. The states are always set here, but only styled below --lg in the CSS.
    function initScrollUpReveal(navigation, isMenuOpen) {
        // Holds the header's place in the flow while it is fixed, and marks where it belongs
        var spacer = document.createElement("div");
        spacer.className = "top-bar-navigation-spacer";
        spacer.setAttribute("aria-hidden", "true");
        navigation.parentNode.insertBefore(spacer, navigation);

        // Ignores small scroll jitter, e.g. from iOS Safari's toolbars
        var threshold = 8;
        var lastScrollY = window.scrollY;
        var ticking = false;

        var update = function () {
            ticking = false;

            var scrollY = Math.max(window.scrollY, 0);
            var delta = scrollY - lastScrollY;
            var flowTop = spacer.getBoundingClientRect().top + scrollY;
            var isPinned = navigation.classList.contains("is-pinned");

            // The page doesn't scroll while the menu is open
            if (isMenuOpen()) {
                lastScrollY = scrollY;
                return;
            }

            if (isPinned && scrollY <= flowTop) {
                // Back at its place in the flow
                navigation.classList.remove("is-pinned", "is-hidden", "is-animated");
            } else if (!isPinned && scrollY > flowTop + navigation.offsetHeight) {
                // Out of view: pin it hidden, then turn on the transition so the change isn't animated
                navigation.classList.add("is-pinned", "is-hidden");
                navigation.getBoundingClientRect();
                navigation.classList.add("is-animated");
            } else if (isPinned && Math.abs(delta) >= threshold) {
                navigation.classList.toggle("is-hidden", delta > 0);
            } else if (Math.abs(delta) < threshold) {
                return;
            }

            lastScrollY = scrollY;
        };

        window.addEventListener("scroll", function () {
            if (!ticking) {
                ticking = true;
                window.requestAnimationFrame(update);
            }
        }, { passive: true });

        // Keyboard users tabbing into a hidden header should see it
        navigation.addEventListener("focusin", function () {
            navigation.classList.remove("is-hidden");
        });

        update();
    }

    function initNavigation(navigation) {
        publishHeight(navigation);
        initScrollUpReveal(navigation, function () {
            return navigation.classList.contains("is-open");
        });

        var toggle = navigation.querySelector(".top-bar-navigation__mobile-toggle");
        var menu = navigation.querySelector(".top-bar-navigation__mobile");

        if (!toggle || !menu) {
            return;
        }

        // Stagger index for the fade-up animation, in document order
        var animatedItems = menu.querySelectorAll(
            ".top-bar-navigation__mobile-list > li, .top-bar-navigation__mobile-divider, .top-bar-navigation__mobile-footer"
        );
        animatedItems.forEach(function (item, index) {
            item.style.setProperty("--i", index);
        });

        // The menu is hidden with visibility/opacity (not display) so it can animate.
        // inert keeps the closed menu out of the tab order and accessibility tree.
        menu.hidden = false;
        menu.inert = true;

        // From --sm up the bottom links share a row with the logo. Links that would wrap to a second
        // row are hidden instead, and the hamburger is shown so they can still be reached in the menu.
        var bottomList = navigation.querySelector(".top-bar-navigation__bottom-list");

        var updateOverflow = function () {
            if (!bottomList) {
                return;
            }

            var items = Array.prototype.slice.call(bottomList.children);
            items.forEach(function (item) {
                item.hidden = false;
            });
            navigation.classList.remove("has-overflow");

            // The list is hidden below the --sm breakpoint, where the hamburger is always shown
            if (!items.length || bottomList.getClientRects().length === 0) {
                return;
            }

            var isWrapped = function (item) {
                return item.offsetTop > items[0].offsetTop;
            };

            if (!items.some(isWrapped)) {
                return;
            }

            // Makes room for the hamburger, which can push more links onto the second row
            navigation.classList.add("has-overflow");
            items.filter(isWrapped).forEach(function (item) {
                item.hidden = true;
            });
        };

        updateOverflow();

        // Link widths change once the web font has loaded
        if (document.fonts) {
            document.fonts.ready.then(updateOverflow);
        }

        var isOpen = function () {
            return toggle.getAttribute("aria-expanded") === "true";
        };

        // Lets the menu fill the remaining viewport height below the header
        var updateOffset = function () {
            navigation.style.setProperty("--cmp-top-bar-navigation-offset", navigation.getBoundingClientRect().bottom + "px");
        };

        // Stops the page behind the full-height menu from scrolling
        var setPageScrollLocked = function (locked) {
            document.documentElement.style.overflow = locked ? "hidden" : "";
        };

        // Remembered because CSS hides the menu at the breakpoint before the resize handler
        // runs, by which time the browser may already have moved focus to <body>
        var lastFocusedInMenu = null;

        var closeMenu = function () {
            toggle.setAttribute("aria-expanded", "false");
            menu.inert = true;
            lastFocusedInMenu = null;
            navigation.classList.remove("is-open");
            setPageScrollLocked(false);
        };

        var openMenu = function () {
            updateOffset();
            toggle.setAttribute("aria-expanded", "true");
            menu.inert = false;
            navigation.classList.add("is-open");
            setPageScrollLocked(true);
        };

        toggle.addEventListener("click", function () {
            if (isOpen()) {
                closeMenu();
            } else {
                openMenu();
            }
        });

        menu.addEventListener("click", function (event) {
            var clickedLink = event.target && event.target.closest && event.target.closest("a");

            if (clickedLink) {
                closeMenu();
            }
        });

        document.addEventListener("keydown", function (event) {
            if (event.key === "Escape" && isOpen()) {
                closeMenu();
                toggle.focus();
            }
        });

        // The open menu covers the page, so close it when focus moves past it into the content
        // behind. relatedTarget is null when the whole window loses focus, which shouldn't close it.
        navigation.addEventListener("focusout", function (event) {
            if (isOpen() && event.relatedTarget && !navigation.contains(event.relatedTarget)) {
                closeMenu();
            }
        });

        menu.addEventListener("focusin", function (event) {
            lastFocusedInMenu = event.target;
        });

        // When the menu closes on resize to desktop, focus inside it would be lost.
        // Move it to the matching desktop link, or the first visible link in the header.
        var moveFocusOutOfMenu = function (focused) {
            var active = document.activeElement;
            var focusWasInMenu = !active || active === document.body || menu.contains(active);

            if (!focused || !focusWasInMenu) {
                return;
            }

            var isVisible = function (element) {
                return !menu.contains(element) && element.getClientRects().length > 0;
            };
            var links = Array.prototype.filter.call(navigation.querySelectorAll("a[href]"), isVisible);
            var match = links.filter(function (link) {
                return link.getAttribute("href") === focused.getAttribute("href");
            })[0];
            var target = match || links[0];

            if (target) {
                target.focus();
            }
        };

        // Handles rotation and resizing while open. The toggle is hidden by CSS at the --sm
        // breakpoint (unless links overflow), so checking it keeps the breakpoint defined in one place.
        window.addEventListener("resize", function () {
            updateOverflow();

            if (!isOpen()) {
                return;
            }

            if (getComputedStyle(toggle).display === "none") {
                var focused = lastFocusedInMenu;
                closeMenu();
                moveFocusOutOfMenu(focused);
            } else {
                updateOffset();
            }
        });
    }
